import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Popover, PopoverModule } from 'primeng/popover';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ButtonModule } from 'primeng/button';

// Modèles
import { AccountingPeriod } from 'src/app/@shared/models/accounting/accounting-period.model';

// Services
import { AccountingPeriodService } from 'src/app/@core/services/accounting/accounting-period.service';

// Utils
import { ExportPeriod } from 'src/app/@shared/utilities/exports/export-period';
import { Sorter } from 'src/app/@shared/utilities/models/sorter';

@Component({
    selector: 'app-date-range-filter',
    imports: [
        FormsModule,
        PopoverModule,
        FloatLabelModule,
        InputTextModule,
        SelectModule,
        DatePickerModule,
        IconFieldModule,
        InputIconModule,
        ButtonModule
    ],
    templateUrl: './date-range-filter.component.html',
    styleUrl: './date-range-filter.component.scss'
})
export class DateRangeFilterComponent implements OnInit, OnChanges {
    @ViewChild('panel') panel!: Popover;

    @Input() label: string = 'Période';
    @Input() range: [Date | null, Date | null] | null = null;
    @Input() showClear: boolean = true;

    @Output() rangeChange = new EventEmitter<[Date | null, Date | null] | null>();
    @Output() cleared = new EventEmitter<void>();

    public periods: ExportPeriod[] = [];
    public selectedPeriod: ExportPeriod | null = null;
    public localDateFrom: Date | null = null;
    public localDateTo: Date | null = null;

    constructor(private accountingPeriodService: AccountingPeriodService) { }

    /*----------------------------------------------------------------------------------------
     * Cycle de vie
     ----------------------------------------------------------------------------------------*/

    public ngOnInit(): void {
        this._initializePeriods();
        this._initializeFromInputs();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['range'] && !changes['range'].firstChange) {
            this._initializeFromInputs();
        }
    }

    /*----------------------------------------------------------------------------------------
     * Propriétés calculées
     ----------------------------------------------------------------------------------------*/

    /**
     * Retourne le texte affiché dans l'input
     */
    public get displayValue(): string {
        if (!this.localDateFrom && !this.localDateTo) {
            return '';
        }

        if (this.localDateFrom && this.localDateTo) {
            const fromStr = this._formatDate(this.localDateFrom);
            const toStr = this._formatDate(this.localDateTo);

            if (fromStr === toStr) {
                return fromStr;
            }

            return `${fromStr} - ${toStr}`;
        }

        if (this.localDateFrom) {
            return `À partir du ${this._formatDate(this.localDateFrom)}`;
        }

        if (this.localDateTo) {
            return `Jusqu'au ${this._formatDate(this.localDateTo)}`;
        }

        return '';
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /**
     * Ouvre le panel
     */
    public openPanel(event: Event): void {
        this.panel.toggle(event);
    }

    /**
     * Appelé quand la période préprogrammée change
     */
    public onPeriodChange(): void {
        if (!this.selectedPeriod) {
            return;
        }

        // Convertir les dates moment en Date JavaScript
        this.localDateFrom = this.selectedPeriod.dateFrom.toDate();
        this.localDateTo = this.selectedPeriod.dateTo.toDate();

        // Émettre immédiatement les changements
        this._emitChanges();
    }

    /**
     * Appelé quand dateFrom change
     */
    public onDateFromChange(): void {
        // Réinitialiser la période sélectionnée car l'utilisateur modifie manuellement
        this.selectedPeriod = null;
        this._emitChanges();
    }

    /**
     * Appelé quand dateTo change
     */
    public onDateToChange(): void {
        // Réinitialiser la période sélectionnée car l'utilisateur modifie manuellement
        this.selectedPeriod = null;
        this._emitChanges();
    }

    /**
     * Réinitialise le filtre (méthode publique pour usage externe)
     */
    public clear(): void {
        this.selectedPeriod = null;
        this.localDateFrom = null;
        this.localDateTo = null;
    }

    /**
     * Réinitialise le filtre (appelé depuis le template)
     */
    public onClear(): void {
        this.clear();
        this._emitChanges();
        this.cleared.emit();
        this.panel.hide();
    }

    /**
     * Réinitialise le filtre depuis l'icône de l'input (sans ouvrir le panel)
     */
    public onClearFromInput(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.clear();
        this._emitChanges();
        this.cleared.emit();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Initialise les périodes préprogrammées
     */
    private _initializePeriods(): void {
        this.accountingPeriodService.getList$({
            selects: ['dateFrom', 'dateTo'],
            sorters: [
                new Sorter({
                    property: 'dateFrom',
                    descending: true
                })
            ]
        }).subscribe((accountingPeriods: AccountingPeriod[]) => {
            // Récupérer toutes les périodes sauf "Tout" (all)
            this.periods = ExportPeriod.getPeriods().filter(p => p.code !== 'all');

            // Ajouter les périodes comptables à la fin de la liste
            for (let i = 0; i < accountingPeriods.length; i++) {
                let label = 'Exercice ';
                if (i === 0) {
                    label += 'en cours';
                } else {
                    label += `N-${i}`;
                }
                this.periods.push(new ExportPeriod(
                    `accounting-${accountingPeriods[i].id}`,
                    label,
                    accountingPeriods[i].dateFrom,
                    accountingPeriods[i].dateTo
                ));
            }
        });
    }

    /**
     * Initialise les valeurs locales depuis les inputs
     */
    private _initializeFromInputs(): void {
        if (this.range && this.range.length === 2) {
            this.localDateFrom = this.range[0];
            this.localDateTo = this.range[1];
        } else {
            this.localDateFrom = null;
            this.localDateTo = null;
        }

        // Essayer de trouver la période correspondante
        this._matchPeriod();
    }

    /**
     * Essaie de trouver une période préprogrammée correspondant aux dates sélectionnées
     */
    private _matchPeriod(): void {
        if (!this.localDateFrom || !this.localDateTo) {
            this.selectedPeriod = null;
            return;
        }

        // Comparer avec les périodes préprogrammées
        const matchingPeriod = this.periods.find(period => {
            const periodFrom = period.dateFrom.toDate();
            const periodTo = period.dateTo.toDate();

            return this._isSameDay(periodFrom, this.localDateFrom!) &&
                this._isSameDay(periodTo, this.localDateTo!);
        });

        this.selectedPeriod = matchingPeriod || null;
    }

    /**
     * Vérifie si deux dates sont le même jour
     */
    private _isSameDay(date1: Date, date2: Date): boolean {
        return date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
    }

    /**
     * Émet les changements vers le parent
     */
    private _emitChanges(): void {
        const newRange: [Date | null, Date | null] | null =
            (this.localDateFrom || this.localDateTo)
                ? [this.localDateFrom, this.localDateTo]
                : null;

        this.rangeChange.emit(newRange);
    }

    /**
     * Formate une date pour l'affichage
     */
    private _formatDate(date: Date): string {
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(date);
    }
}
